import { openDB } from "idb";

// Local-first cache for scribe-fe (card #419).
//
// IndexedDB is the source of truth the UI reads from; the network is a
// background sync engine that hydrates these caches (pull) and drains the
// outbox (push). This module owns the schema + a small repository API — no
// other file should call `idb` directly.
//
//   entity stores  — shows/classes/rides/tests/people/horses/scoreSheets,
//                    keyed by server id, filled by hydration.
//   outbox         — ordered log of pending mutations to push to score-api.
//   meta           — sync cursors + counters (e.g. the outbox sequence).
//   scoreSheetSaves— legacy whole-sheet retry queue (#364); retained and
//                    migrated forward until the outbox write-path (#422)
//                    supersedes it.

export const DB_NAME = "scribe-offline";
// v3 → v4 (card #139): `utilityEvents` — the last GET /ride/:id/utility-events
// poll result, keyed by rideId, so a reload during an offline period still
// shows the last-known BLOOD/LAMENESS flags instead of a blank banner.
// v4 → v5 (card #761): `judges` — a show's score-judge roster (GET /judge),
// cached so section-judge gating (Class.sectionJudgeAssignments, #759) can
// resolve a booth position's judge identity offline, the same way
// classes/rides/tests already work offline-first.
// v5 → v6 (card #1664): `catalogDefs` — the global test catalog (GET
// /catalog, ~148 rows, org-agnostic), same store shape as scoreadmin-fe's
// (card #1662). Deliberately a SEPARATE store from `tests` above: `tests` is
// the per-show, reconcile-enriched score-test rows scoring already depends
// on (GET /test/:id, sync.js); `catalogDefs` is the raw global test
// DEFINITIONS used only to resolve a display NAME off ride.testId/
// class.testId (classDisplay.js's resolveCatalogTestName/getRideTestName).
// See hooks/useCatalogDefs.js for the hydrate-once policy and the demotion
// order (table cache preferred once present, a caller-supplied fallback off
// the ride's own Test row for a scribe who's never been online since install
// — the build-time score-common/catalog bundle that used to fill that role
// retired in #1665, docs/score/catalog-authority.md section 5).
// v6 → v7 (card #2361): `scribeSeats` — a durable, scribe-scoped record of
// "this sub is assigned to this class at these positions", written whenever
// the assignment is actually seen (login-time resolution, ClassList visits)
// and read by useAssignedPosition when the CLASS itself is a cold-cache miss
// offline. Deliberately a separate store from `classes`, not a second writer
// into it: `classes` rows written by different sources carry different
// completeness (sync.js's hydrator writes raw, unstamped rows; a scribe's own
// listClasses/batch fetch is fuller but POST /class/batch — see
// useScribeAssignments — returns RAW rows missing fields listClasses adds,
// e.g. scheduledTime/fvdPushState, so writing those into the shared store
// would downgrade an already-enriched cached row for every reader of
// `classes.get`). A scribe's own assignment doesn't need a full class row at
// all — just classId + position — so it gets its own tiny store instead.
export const DB_VERSION = 7;

// keyPath + optional indexes per store. Index value is the keyPath for that
// index (an array keyPath makes a compound index).
//
// #695 — `classes.showScribe` is a compound index on [showId, cachedForSub],
// the same pattern as `scoreSheets.rideJudge` below. `cachedForSub` is a
// write-time tag (see the `classes` repository export) stamping WHICH
// scribe's fetch cached a given row. The background hydrator (sync.js)
// writes classes via the plain `putAll` and never stamps this field, so its
// rows are simply invisible to the compound index (IndexedDB excludes a
// record from an index when any component of a compound keyPath is
// undefined) — they can never surface on a scribe-scoped read, regardless of
// which scribe(s) previously used this device.
export const STORES = {
  shows: { keyPath: "id" },
  classes: {
    keyPath: "id",
    indexes: { showId: "showId", showScribe: ["showId", "cachedForSub"] },
  },
  rides: { keyPath: "id", indexes: { classId: "classId" } },
  tests: { keyPath: "id" },
  people: { keyPath: "id" },
  horses: { keyPath: "id" },
  scoreSheets: { keyPath: "id", indexes: { rideJudge: ["rideId", "judgeId"] } },
  outbox: { keyPath: "opId", indexes: { seq: "seq" } },
  meta: { keyPath: "key" },
  scoreSheetSaves: { keyPath: "key" },
  // card #139 — last-known utility events per ride, { rideId, events, updatedAt }.
  utilityEvents: { keyPath: "rideId" },
  // card #761 — score-judge roster rows (GET /judge), keyed by id, indexed by
  // showId so section-judge gating can resolve "who's the judge at position
  // C" offline via name-matching against Class.judgeAssignments.
  judges: { keyPath: "id", indexes: { showId: "showId" } },
  // card #1664 — GET /catalog, keyed by the def's stable slug id (same key
  // ride.testId/class.testId already resolve against, see
  // classDisplay.js's resolveCatalogTestName). Global, not org/show-scoped:
  // no index needed.
  catalogDefs: { keyPath: "id" },
  // card #2361 — this scribe's own class-assignment record, keyed by
  // `${classId}::${sub}` (a shared kiosk can hold more than one scribe's
  // rows). See the DB_VERSION comment above for why this is a separate store
  // from `classes` rather than a second writer into it.
  scribeSeats: { keyPath: "key" },
};

let dbPromise;

export function openDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        for (const [name, cfg] of Object.entries(STORES)) {
          const store = db.objectStoreNames.contains(name)
            ? tx.objectStore(name)
            : db.createObjectStore(name, { keyPath: cfg.keyPath });
          for (const [idxName, keyPath] of Object.entries(cfg.indexes ?? {})) {
            if (!store.indexNames.contains(idxName)) {
              store.createIndex(idxName, keyPath);
            }
          }
        }
        if (oldVersion >= 1) await migrateSavesToOutbox(tx);
      },
    });
  }
  return dbPromise;
}

// v1 → v2: fold any parked whole-sheet saves into the outbox so the sync
// engine can drain them. Copies (does not delete) — the legacy drain still
// reads scoreSheetSaves until #422 removes it, so nothing is stranded.
async function migrateSavesToOutbox(tx) {
  const saves = await tx.objectStore("scoreSheetSaves").getAll();
  if (!saves.length) return;
  const outbox = tx.objectStore("outbox");
  let seq = 0;
  for (const s of saves) {
    seq += 1;
    const entityKey = `${s.rideId}::${s.judgeId}`;
    const isTemp = typeof s.rideId === "string" && s.rideId.startsWith("temp");
    await outbox.put({
      opId: `scoreSheet:${entityKey}`,
      seq,
      type: s.serverId ? "updateScoreSheet" : "createScoreSheet",
      entityKey,
      tempId: isTemp ? s.rideId : null,
      payload: {
        rideId: s.rideId,
        judgeId: s.judgeId,
        serverId: s.serverId ?? null,
        body: s.body,
      },
      status: "pending",
      attempts: 0,
      createdAt: s.updatedAt ?? null,
    });
  }
  await tx.objectStore("meta").put({ key: "outboxSeq", value: seq });
}

// ── Generic repository ────────────────────────────────────────────────────

export async function get(store, key) {
  return (await openDb()).get(store, key);
}

export async function getAll(store) {
  return (await openDb()).getAll(store);
}

export async function put(store, value) {
  return (await openDb()).put(store, value);
}

export async function bulkPut(store, values) {
  if (!values?.length) return;
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  for (const v of values) tx.store.put(v);
  await tx.done;
}

export async function queryByIndex(store, index, value) {
  return (await openDb()).getAllFromIndex(store, index, value);
}

export async function remove(store, key) {
  return (await openDb()).delete(store, key);
}

export async function bulkRemove(store, keys) {
  if (!keys?.length) return;
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  for (const k of keys) tx.store.delete(k);
  await tx.done;
}

export async function clear(store) {
  return (await openDb()).clear(store);
}

// ── Meta (sync cursors / counters) ─────────────────────────────────────────

export async function getMeta(key) {
  const row = await get("meta", key);
  return row ? row.value : undefined;
}

export async function setMeta(key, value) {
  return put("meta", { key, value });
}

// ── Outbox (ordered pending mutations) ─────────────────────────────────────

async function nextSeq(db) {
  const tx = db.transaction("meta", "readwrite");
  const cur = (await tx.store.get("outboxSeq"))?.value ?? 0;
  const next = cur + 1;
  tx.store.put({ key: "outboxSeq", value: next });
  await tx.done;
  return next;
}

// ── Ownership (card #1923) ───────────────────────────────────────────────
//
// The structural half of the WEC 2026-08-23 sync incident: scribe A finishes
// a class with writes still queued, signs out (which deliberately does NOT
// touch the outbox, #519/#1289), and scribe B signs in on the same kiosk —
// B's token would otherwise replay A's queued writes. The fix is an owner on
// every op, stamped at enqueue() time from whoever is CURRENTLY signed in.
//
// This module has no React/auth dependency of its own (it's imported by
// framework-agnostic sync.js and by plain store modules like manualRides.js
// that aren't hooks), so ScoreContext pushes the signed-in sub in here the
// same way it already pushes into syncEngine (setApi/setBrowserOnline) —
// see ScoreContext's effect on auth.user?.profile?.sub.
let currentOwnerSub = null;

export function setCurrentOwnerSub(sub) {
  currentOwnerSub = sub ?? null;
}

export function getCurrentOwnerSub() {
  return currentOwnerSub;
}

// An op belongs to "someone else" whenever its owner isn't EXACTLY the given
// sub. A missing `owner` (any row written before this card, or by the v1→v2
// scoreSheetSaves migration below, which never went through enqueue) is
// "unknown" — AC2 requires that never be read as "belongs to whoever happens
// to be signed in now".
//
// #1923 F2 (judge finding, fixed) — fail CLOSED: an op is held whenever its
// owner doesn't exactly match a KNOWN current sub. A falsy currentSub means
// no sub is known yet (signed out, or ScoreContext hasn't resolved a sub),
// so nothing can be verified as "mine" and everything is held, including
// ops whose own `owner` is also null/undefined — an unknown current sub
// never gets to wave an unknown-owner op through. `flush()` additionally
// bails out entirely when getCurrentOwnerSub() is null, so nothing that
// needs a bearer token even attempts to send while no one is signed in.
//
// This used to fail OPEN (`Boolean(currentSub) && ...`), which let every
// foreign/legacy op read as sendable while no current sub was known —
// exactly the hole AC2 names. The earlier fail-closed attempt was reverted
// because it broke 31 tests in sync.test.js/manualRideSyncStatus.test.js
// that build a syncEngine directly and never call setCurrentOwnerSub; those
// suites now seed an owner sub in setup (they mean "a scribe is signed in"),
// so the fail-closed behavior here is no longer in tension with them.
//
// #1923 F3 (judge finding, fixed) — `apiRef` is also NOT the safety net an
// earlier version of this comment claimed: ScoreContext.jsx wires
// syncEngine.setApi(apiAdapter) unconditionally on mount (a plain useEffect
// with no auth.user guard), and apiAdapter builds its Configuration with
// accessToken: undefined when there's no signed-in user yet — so flush()
// can have apiRef set with no real sub resolved. That claim has been
// removed; nothing in this file leans on it anymore.
export function isHeldOp(op, currentSub) {
  if (!currentSub) return true;
  return (op?.owner ?? null) !== currentSub;
}

// #2031 AC2 — the hard boundary a claim action must respect: an op is
// claimable only when it carries NO owner at all (a pre-#1923 legacy row, or
// the v1->v2 scoreSheetSaves migration below, neither of which ever went
// through enqueue's owner stamp). An op stamped with a DIFFERENT KNOWN sub is
// held for the same reason isHeldOp holds it, but must never read as
// claimable — conflating the two would let a scribe claim someone else's
// STILL-owned queued writes, the exact WEC 2026-08-23 hazard #1923 exists to
// prevent. Deliberately takes no currentSub: ownerless-ness is a fact about
// the op alone, true or false regardless of who is signed in right now.
export function isOwnerless(op) {
  return (op?.owner ?? null) === null;
}

// #2031 AC3 / #882 — a dependent op (carries a tempId, not itself a
// createRide) can never drain while its own createRide op is still queued:
// this is the exact same ordering-gate check flush()'s `ready` filter makes
// (see sync.js's #882 CRITICAL invariant comment) — pulled out here so the
// engine and the display-side "was this op withheld" logic share one
// definition instead of two copies of the same tempId-segment match
// drifting apart. Matches on the createRide op being present AT ALL,
// regardless of ITS status (pending, error, or terminal): a terminal
// createRide (a permanent 4xx, never auto-retried, #1921) still sits in the
// outbox until a human discards it, so it still blocks its dependent op —
// that is the "blocked forever" case the card names.
export function isBlockedByUnsyncedRide(op, allOps) {
  if (!op?.tempId || op.type === "createRide") return false;
  return allOps.some(
    (other) => other.type === "createRide" && other.tempId === op.tempId,
  );
}

// Enqueue or upsert an op. If `opId` already exists the payload is replaced
// but its original `seq` (FIFO position) is preserved — last-write-wins per
// entity, so a fresh score save overwrites the parked one without reordering.
// `owner` is always stamped fresh from the CURRENT sub (#1923): an upsert is
// always a new local write by whoever is signed in right now, same as status
// resetting to "pending" below.
export async function enqueue({ opId, type, entityKey, tempId = null, payload }) {
  const db = await openDb();
  const existing = await db.get("outbox", opId);
  const seq = existing ? existing.seq : await nextSeq(db);
  const op = {
    opId,
    seq,
    type,
    entityKey,
    tempId,
    payload,
    status: "pending",
    attempts: existing?.attempts ?? 0,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    owner: currentOwnerSub,
  };
  await db.put("outbox", op);
  return op;
}

// Pending ops in FIFO order (ascending seq).
export async function peekOutbox() {
  return (await openDb()).getAllFromIndex("outbox", "seq");
}

export async function getOutboxOp(opId) {
  return (await openDb()).get("outbox", opId);
}

export async function removeOutboxOp(opId) {
  return (await openDb()).delete("outbox", opId);
}

export async function updateOutboxOp(opId, patch) {
  const db = await openDb();
  const existing = await db.get("outbox", opId);
  if (!existing) return null;
  const merged = { ...existing, ...patch };
  await db.put("outbox", merged);
  return merged;
}

// ── Per-entity accessors (thin wrappers used by the read hooks in #421) ─────

export const shows = {
  get: (id) => get("shows", id),
  getAll: () => getAll("shows"),
  // #1736 — org-scoped read for useShows. This store is a CROSS-ORG cache:
  // putAll below is an upsert that never evicts, so shows from every org the
  // device has ever loaded accumulate in it forever. An unscoped getAll
  // therefore keeps serving the PREVIOUS org's shows after a switch, and lets
  // a scribe pick a show that isn't in the active org at all. Same scoping
  // intent as classes' byShowForScribe (#695), filtered in JS rather than
  // behind a new IndexedDB index because an index needs a schema version bump
  // and the show list is only tens of rows.
  // A falsy orgId matches NOTHING rather than every row that happens to lack
  // the field: `r.orgId === undefined` would otherwise make a pre-orgId legacy
  // row look like a hit for "no active org yet".
  //
  // #1746 — #1736 added no IndexedDB migration and did not bump DB_VERSION,
  // so a device that cached shows BEFORE v0.29.1 has rows with no `orgId` at
  // all, which can never match the filter above. Online this heals itself
  // (putAll re-stamps orgId from score-api's response on the next fetch), but
  // offline the scoped read comes back empty and the show picker has nothing
  // to fall back on: a regression against pre-#1736 behaviour. So: if the
  // org-scoped read comes back empty AND the store holds rows with no orgId
  // at all, serve those legacy rows instead of nothing.
  //
  // Read-time fallback was chosen over a DB_VERSION migration. Rejected
  // reason is NOT that the active org is unknowable at upgrade time --
  // ActiveOrgContext.jsx persists it synchronously to localStorage
  // (`common-fe.activeOrgId`), so an upgrade callback could read it. The
  // real problem is what pre-#1736 rows the migration would be stamping: per
  // the comment above, this store is a cross-org pile that never evicts, so
  // a multi-org device's legacy rows can belong to MORE than one org. Any
  // single org id the migration picks would mislabel every other org's rows
  // in that pile as the chosen org's own. A WRONGLY-stamped row is worse
  // than an unstamped one: the fallback below only ever serves `!r.orgId`
  // rows, so it can never catch a mislabeled row, which is instead served to
  // the wrong org as a legitimate scoped match. A later fetch does re-stamp
  // (see the putAll note further down), but only for rows that org's current
  // list still returns -- a mislabeled row for a show no org's list returns
  // any more is never corrected at all. Dropping the rows instead just
  // recreates the same offline gap once.
  //
  // This read-time fallback accepts a narrower, self-healing version of that
  // same cost instead, same trade the card names explicitly: on a device
  // that cached MORE THAN ONE org's shows before v0.29.1, all of it lives in
  // this store unstamped, so if org B has no stamped rows of its own yet,
  // this fallback cannot tell org A's legacy rows from org B's and will
  // serve org A's pre-v0.29.1 shows under org B. The window closes for the
  // READING org once that org itself gets a real online fetch (it then has
  // stamped rows of its own, so `scoped.length` is non-zero and the fallback
  // never runs for it), or once EVERY OTHER org holding legacy rows in the
  // pile has: putAll re-stamps a fetching org's rows with orgId, so they stop
  // matching the `!r.orgId` filter and stop being eligible for anyone else's
  // fallback. Note one other org's fetch is NOT enough on its own: with three
  // or more pre-v0.29.1 orgs in the pile, org A's fetch clears only org A's
  // rows, and org C's legacy rows are still served under org B until org B or
  // org C fetches too. It never widens #1736's fix for the case #1736
  // actually covers (rows already carrying an orgId): only orgId-less rows
  // are ever served this way, never another org's orgId-stamped rows.
  byOrg: async (orgId) => {
    if (!orgId) return [];
    const all = await getAll("shows");
    const scoped = all.filter((r) => r?.orgId === orgId);
    if (scoped.length) return scoped;
    return all.filter((r) => r && !r.orgId);
  },
  putAll: (rows) => bulkPut("shows", rows),
};

export const classes = {
  get: (id) => get("classes", id),
  byShow: (showId) => queryByIndex("classes", "showId", showId),
  // #695 — scribe-scoped read for useClassesForShow: only rows THIS scribe's
  // own fetch tagged (see putAllForScribe) can ever come back here, so a
  // stale/cross-scribe row from a previous login on a shared device can't
  // surface even before any client-side assignment filter runs.
  byShowForScribe: (showId, sub) => queryByIndex("classes", "showScribe", [showId, sub]),
  put: (row) => put("classes", row),
  putAll: (rows) => bulkPut("classes", rows),
  // #695 — like putAll, but stamps each row with the scribe (`sub`) whose
  // fetch produced it, so byShowForScribe can find it again for THAT scribe
  // only. Used by useClassesForShow; the generic background hydrator
  // (sync.js) intentionally keeps using plain putAll (unscoped, broad
  // offline cache — a different purpose than the "My Classes" screen).
  putAllForScribe: (rows, sub) =>
    bulkPut(
      "classes",
      (rows ?? []).map((row) => ({ ...row, cachedForSub: sub })),
    ),
};

// card #2361 — this scribe's own class-assignment record: classId + which
// position(s) they're seated at, WITHOUT a full class row. Populated wherever
// the assignment is actually observed (useScribeAssignments at login,
// useClassesForShow on a ClassList visit) so useAssignedPosition can resolve
// a cold-class-cache offline read without depending on the (possibly never
// warm) `classes` store at all. See the DB_VERSION/STORES comments above for
// why this is its own store.
export const scribeSeats = {
  get: (classId, sub) => get("scribeSeats", `${classId}::${sub}`),
  // rows: [{ classId, positions }]. A class with zero positions for this sub
  // is simply not written — an absent row and a written-empty row must read
  // the same (both "no local record"), so there's no reason to store the
  // second shape.
  putAllForScribe: (rows, sub) =>
    bulkPut(
      "scribeSeats",
      (rows ?? [])
        .filter((r) => r.classId && r.positions?.length)
        .map((r) => ({ key: `${r.classId}::${sub}`, classId: r.classId, sub, positions: r.positions })),
    ),
  // card #2361 — deletes this scribe's own seat row for classIds where a
  // fresh, UNFILTERED read (useScribeAssignments's listClassesForShows,
  // which returns every class across the org's shows regardless of who's
  // assigned) positively confirms the sub is no longer named in
  // scribeAssignments at all. Without this a revoked assignment's row
  // survives forever: seatFallback (useAssignedPosition) would keep
  // resolving it offline against a cold class row, letting a former scribe
  // start (and, on reconnect, sync) a scoresheet for a seat that isn't
  // theirs any more. useClassesForShow must NEVER call this — its own fetch
  // is `mine=true`-scoped, so a class's absence there proves nothing about
  // whether the assignment was actually revoked.
  pruneForScribe: (classIds, sub) =>
    bulkRemove(
      "scribeSeats",
      (classIds ?? []).map((classId) => `${classId}::${sub}`),
    ),
};

// card #761 — a show's score-judge roster, cache-first like classes/rides.
export const judges = {
  get: (id) => get("judges", id),
  byShow: (showId) => queryByIndex("judges", "showId", showId),
  putAll: (rows) => bulkPut("judges", rows),
};

// card #1664 — the global test catalog. Hydration policy: refetch at EVERY
// app-init, not gated on the store already being non-empty (see
// hooks/useCatalogDefs.js — this deliberately FORKS from scoreadmin-fe's
// once-if-empty policy, card #1662, so an admin's correction reaches an
// already-hydrated scribe on the next restart with no redeploy). Reads stay
// cache-first regardless: `putAll` below only runs when the refetch
// actually returns rows, so a genuinely empty GET /catalog response (`[]`)
// or a failed/hung request simply leaves the existing store untouched
// rather than wiping it.
export const catalogDefs = {
  get: (id) => get("catalogDefs", id),
  getAll: () => getAll("catalogDefs"),
  putAll: (rows) => bulkPut("catalogDefs", rows),
};

export const rides = {
  get: (id) => get("rides", id),
  byClass: (classId) => queryByIndex("rides", "classId", classId),
  put: (row) => put("rides", row),
  putAll: (rows) => bulkPut("rides", rows),
  remove: (id) => remove("rides", id),
};

export const tests = {
  get: (id) => get("tests", id),
  put: (row) => put("tests", row),
  putAll: (rows) => bulkPut("tests", rows),
};

export const people = {
  get: (id) => get("people", id),
  getAll: () => getAll("people"),
  // #906 — useScoreSheet's fromApi() write-through calls this singular put()
  // on every revalidation that resolves a rider; it was missing (only
  // putAll existed), so that TypeError silently aborted fromApi() before its
  // apply(apiCtx) ever landed — stranding the sheet on stale cached data
  // (e.g. a class fetched before judgeIds was added, #733) forever.
  put: (row) => put("people", row),
  putAll: (rows) => bulkPut("people", rows),
};

export const horses = {
  get: (id) => get("horses", id),
  getAll: () => getAll("horses"),
  // #906 — see people.put above; same missing method, same silent abort.
  put: (row) => put("horses", row),
  putAll: (rows) => bulkPut("horses", rows),
};

export const scoreSheets = {
  get: (id) => get("scoreSheets", id),
  byRideJudge: (rideId, judgeId) =>
    queryByIndex("scoreSheets", "rideJudge", [rideId, judgeId]),
  put: (row) => put("scoreSheets", row),
  putAll: (rows) => bulkPut("scoreSheets", rows),
  // card #1922 — every cached score sheet for a ride, any judge. Used by the
  // manual-ride discard guard (store/manualRides.js), which needs "does this
  // ride have ANY score data" without knowing which judge scored it. No new
  // index: rideJudge is compound on [rideId, judgeId] so it can't answer a
  // rideId-only query, and this store is small enough to filter in JS rather
  // than justify a DB_VERSION bump for a rideId-alone index.
  forRide: async (rideId) =>
    (await getAll("scoreSheets")).filter((s) => s?.rideId === rideId),
};

// card #139 — last-known GET /ride/:id/utility-events result per ride, so a
// reload during an offline period shows what was last seen rather than
// nothing. `row` is { rideId, events, updatedAt }.
export const utilityEvents = {
  get: (rideId) => get("utilityEvents", rideId),
  put: (row) => put("utilityEvents", row),
};

// #2111 — the WHOLE local database, every store in STORES (entity caches,
// the outbox, meta, everything), as one plain object keyed by store name.
// Replaces the old "Copy outbox" export (formatOutboxForExport in
// outboxDetail.js, which only ever covered the outbox store): a scribe
// diagnosing a stuck kiosk with no devtools needs to hand over more than the
// outbox — a class/ride/scoreSheet cache that never hydrated, or hydrated
// wrong, is just as real a cause of a stuck sync as a bad outbox row, and the
// old export had no way to show that. Read-only; iterates STORES directly
// (not a hardcoded list) so a future store never has to be added here by
// hand to stay covered.
export async function dumpDatabase() {
  const storeNames = Object.keys(STORES);
  const entries = await Promise.all(
    storeNames.map(async (name) => [name, await getAll(name)]),
  );
  return {
    dbName: DB_NAME,
    dbVersion: DB_VERSION,
    exportedAt: new Date().toISOString(),
    stores: Object.fromEntries(entries),
  };
}

// Shared by _resetDbForTests and rebuildDatabase below: drop the cached
// connection and delete the whole DB_NAME database from IndexedDB.
async function closeAndDeleteDb() {
  if (dbPromise) (await dbPromise).close();
  dbPromise = undefined;
  await new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}

// Test-only: drop the cached connection + delete the database so each test
// starts from a known-empty state.
export async function _resetDbForTests() {
  await closeAndDeleteDb();
  // #1923 — currentOwnerSub is module state, not DB state; a test that stamps
  // an owner (or leaves one signed in) must not leak into the next test.
  currentOwnerSub = null;
}

// #1902 — support-only recovery: delete the whole scribe-offline database and
// reopen it empty. Deliberately does NOT touch currentOwnerSub (unlike
// _resetDbForTests above) — the scribe stays signed in through a rebuild, so
// the outbox's ownership stamping must keep matching whoever is still
// signed in once local/rebuild.js re-hydrates. Callers (local/rebuild.js)
// are responsible for confirming the outbox is empty BEFORE calling this;
// it unconditionally wipes.
export async function rebuildDatabase() {
  await closeAndDeleteDb();
  await openDb();
}
